import { useCallback, useEffect, useState } from 'react';
import { Loader2, Save, AlertCircle, CheckCircle2, Globe } from 'lucide-react';
import { supabase } from '../../utils/supabaseStorage';
import { useAppStore } from '../../store/useAppStore';
import { LoadingSpinner } from '../../components/LoadingSpinner';

const INPUT =
  'w-full bg-elevated border border-subtle rounded-lg px-3 py-2 text-th-primary placeholder-th-tertiary focus:border-brand focus:outline-none transition-all';
const TEXTAREA = `${INPUT} resize-y min-h-[100px]`;

interface ProfileRow {
  hy_user_id: string;
  hy_name: string | null;
  creator_handle: string | null;
  creator_bio: string | null;
  creator_socials: Record<string, string> | null;
}

const SOCIAL_KEYS: Array<{ key: string; label: string; placeholder: string }> = [
  { key: 'twitter', label: 'X / Twitter', placeholder: '@handle or full URL' },
  { key: 'github', label: 'GitHub', placeholder: 'github.com/...' },
  { key: 'website', label: 'Website', placeholder: 'https://...' },
  { key: 'linkedin', label: 'LinkedIn', placeholder: 'linkedin.com/in/...' },
];

export function StudioProfilePage() {
  const session = useAppStore((s) => s.session);
  const [profile, setProfile] = useState<ProfileRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [socials, setSocials] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    if (!session?.hy_user_id) return;
    setLoading(true);
    setError(null);
    const { data, error: e } = await supabase
      .from('users')
      .select('hy_user_id, hy_name, creator_handle, creator_bio, creator_socials')
      .eq('hy_user_id', session.hy_user_id)
      .maybeSingle();
    if (e) {
      setError(e.message);
    } else if (data) {
      const row = data as ProfileRow;
      setProfile(row);
      setSocials((row.creator_socials as Record<string, string>) || {});
    }
    setLoading(false);
  }, [session?.hy_user_id]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleSave = async () => {
    if (!profile) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    const cleanedSocials = Object.fromEntries(
      Object.entries(socials)
        .map(([k, v]) => [k, (v || '').trim()])
        .filter(([, v]) => v.length > 0)
    );
    const { error: e } = await supabase
      .from('users')
      .update({
        creator_bio: profile.creator_bio?.trim() || null,
        creator_socials: Object.keys(cleanedSocials).length > 0 ? cleanedSocials : null,
        updated_at: new Date().toISOString(),
      })
      .eq('hy_user_id', profile.hy_user_id);
    if (e) {
      setError(e.message);
    } else {
      setSuccess('Profile saved.');
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-12 text-center">
        <p className="text-th-secondary">Profile not available.</p>
      </div>
    );
  }

  const handleUrl = profile.creator_handle ? `/c/${profile.creator_handle}` : null;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <main className="min-h-0 flex-1 overflow-y-auto max-w-3xl mx-auto w-full px-4 py-6 space-y-4">
        <header>
          <h1 className="text-xl font-bold text-th-primary tracking-tight">Public profile</h1>
          <p className="text-xs text-th-secondary mt-1">
            Shown on your <span className="font-mono">/c/handle</span> landing page alongside your live agents.
          </p>
        </header>

        {error && (
          <div className="p-3 rounded-lg border border-status-error/30 bg-status-error/10 text-status-error text-sm flex items-start gap-2">
            <AlertCircle className="w-4 h-4 mt-0.5" /> {error}
          </div>
        )}
        {success && (
          <div className="p-3 rounded-lg border border-status-success/30 bg-status-success/10 text-status-success text-sm flex items-start gap-2">
            <CheckCircle2 className="w-4 h-4 mt-0.5" /> {success}
          </div>
        )}

        <section className="card p-4 space-y-3">
          <Field label="Handle">
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={profile.creator_handle ?? ''}
                disabled
                className={`${INPUT} font-mono opacity-70`}
                placeholder="(unset \u2014 ask superadmin to assign one)"
              />
              {handleUrl && (
                <a
                  href={handleUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="button-secondary text-sm inline-flex items-center gap-1.5 shrink-0"
                >
                  <Globe className="w-4 h-4" />
                  View
                </a>
              )}
            </div>
            <p className="text-xs text-th-tertiary">
              Handles are managed by superadmins for now.
            </p>
          </Field>

          <Field label="Display name">
            <input
              type="text"
              value={profile.hy_name ?? ''}
              disabled
              className={`${INPUT} opacity-70`}
            />
          </Field>

          <Field label="Bio" hint="Short paragraph for your public profile.">
            <textarea
              value={profile.creator_bio ?? ''}
              onChange={(e) => setProfile({ ...profile, creator_bio: e.target.value })}
              className={TEXTAREA}
              rows={4}
              maxLength={1000}
              placeholder="Tell users what you build and why."
            />
          </Field>
        </section>

        <section className="card p-4 space-y-3">
          <h2 className="text-sm font-semibold text-th-primary">Links</h2>
          {SOCIAL_KEYS.map((s) => (
            <Field key={s.key} label={s.label}>
              <input
                type="text"
                value={socials[s.key] || ''}
                onChange={(e) => setSocials((prev) => ({ ...prev, [s.key]: e.target.value }))}
                className={INPUT}
                placeholder={s.placeholder}
              />
            </Field>
          ))}
        </section>

        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => void handleSave()}
            disabled={saving}
            className="button-primary text-sm inline-flex items-center gap-1.5"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save profile
          </button>
        </div>
      </main>
    </div>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block space-y-1">
      <span className="text-sm text-th-secondary">{label}</span>
      {children}
      {hint ? <span className="block text-xs text-th-tertiary">{hint}</span> : null}
    </label>
  );
}
